import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Form, Input, Space } from 'antd';
import React, { useEffect, useMemo } from 'react';
import * as urlSlug from 'url-slug';
import { useApp } from '../../../AppContext';
import {
  MAX_LENGTHS,
  MODULES,
  ROUTES,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../../common/constants';
import { formValidatorRules } from '../../../common/utils';
import PageHeader from '../../../components/PageHeader';
import useCheckPermission from '../../../hooks/useCheckPermission';
import { DEFAULT_CONFIG_KEYS } from '../../workspaces/components';
import { ColorPicker, SlugInput } from './components/FormInputs';
import { CREATE_TOPIC, UPDATE_TOPIC } from './graphql/Mutations';
import { GET_TOPIC } from './graphql/Queries';

const COLOR_KEYS = {
  red2: 'red2',
  yellow2: 'yellow2',
  blue2: 'blue2',
  moss2: 'moss2'
};
const initialValues = {
  name: '',
  slug: '/',
  primaryColor: COLOR_KEYS.red2,
  metaHeader: '',
  metaFooter: ''
};

const AddEditTopic = ({ history, match: { params } }) => {
  const { state } = useApp();
  const workspaceColors = state?.workspaceConfig?.find(
    ({ key }) => key === DEFAULT_CONFIG_KEYS.COLORS
  )?.value;

  const [form] = Form.useForm();
  const { topicId } = params;

  const isEdit = !!topicId;

  const [fetchTopicDetails, { loading: fetchingDetails }] = useLazyQuery(
    GET_TOPIC,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [addUpdateTopic, { loading }] = useMutation(
    isEdit ? UPDATE_TOPIC : CREATE_TOPIC
  );

  const colors = useMemo(
    () => [
      {
        key: COLOR_KEYS.red2,
        color: workspaceColors?.[COLOR_KEYS.red2]
      },
      {
        key: COLOR_KEYS.yellow2,
        color: workspaceColors?.[COLOR_KEYS.yellow2]
      },
      {
        key: COLOR_KEYS.blue2,
        color: workspaceColors?.[COLOR_KEYS.blue2]
      },
      {
        key: COLOR_KEYS.moss2,
        color: workspaceColors?.[COLOR_KEYS.moss2]
      }
    ],
    [workspaceColors]
  );

  useEffect(() => {
    if (isEdit && !!topicId) {
      fetchTopicDetails({
        variables: {
          id: topicId
        }
      })
        .then((res) => {
          const topic = res.data?.topicAdmin;
          if (topic) {
            const { name, primaryColor, slug, metaHeader, metaFooter } = topic;
            form.setFieldsValue({
              name,
              primaryColor: primaryColor ?? '',
              slug: slug.startsWith('/') ? slug : `/${slug}`,
              metaHeader: metaHeader ?? '',
              metaFooter: metaFooter ?? ''
            });
          }
        })
        .catch();
    }
  }, [fetchTopicDetails, topicId, form, isEdit]);

  const handleCancel = () => {
    history.push(ROUTES?.TOPICS);
  };

  const handleSubmit = (values) => {
    const slug = values.slug.startsWith('/')
      ? values.slug.substring(1)
      : values.slug;

    addUpdateTopic({
      variables: {
        data: {
          ...values,
          slug
        },
        ...(isEdit && {
          id: topicId
        })
      }
    })
      .then(() => {
        history.push(ROUTES?.TOPICS);
      })
      .catch();
  };

  const handleNameChange = (e) => {
    form?.setFieldValue('slug', `/${urlSlug.convert(e.target.value)}`);
  };

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.LABEL_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.LABEL_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  return (
    <>
      <PageHeader menu={MODULES?.LABELS} />
      <div className="page-wrapper">
        <div className="page-wrapper-body">
          <Form
            className="add-edit-form"
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={initialValues}
            disabled={isViewOnly || fetchingDetails}
          >
            <Form.Item
              label="Name"
              name="name"
              required
              rules={[
                formValidatorRules?.required('Please enter name!'),
                formValidatorRules?.maxLength(MAX_LENGTHS.NAME)
              ]}
            >
              <Input placeholder="Enter name" onChange={handleNameChange} />
            </Form.Item>
            <Form.Item
              label="Slug"
              name="slug"
              rules={[
                {
                  required: true,
                  message: 'Please enter slug!'
                },
                formValidatorRules?.maxLength(MAX_LENGTHS.TITLE)
              ]}
            >
              <SlugInput />
            </Form.Item>
            <Form.Item label="Color" name="primaryColor">
              <ColorPicker data={colors} />
            </Form.Item>
            <Form.Item
              name="metaHeader"
              label="Meta Header"
              rules={[formValidatorRules?.maxLength(MAX_LENGTHS.DESCRIPTION)]}
            >
              <Input.TextArea rows={5} placeholder="Enter meta header" />
            </Form.Item>

            <Form.Item
              name="metaFooter"
              label="Meta Footer"
              rules={[formValidatorRules?.maxLength(MAX_LENGTHS.DESCRIPTION)]}
            >
              <Input.TextArea rows={5} placeholder="Enter meta footer" />
            </Form.Item>
            <div className="d-flex button-section mb-8">
              <Space>
                {isAddEditAllowed && (
                  <Button
                    loading={fetchingDetails || loading}
                    type="text"
                    htmlType="submit"
                    className="text-btn mr-8"
                    size="middle"
                  >
                    Save
                  </Button>
                )}

                <Button
                  disabled={fetchingDetails || loading}
                  type="text"
                  className="text-btn2"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
};

export default AddEditTopic;
